import React, {useRef, useState, useEffect} from 'react'
import {observer} from 'mobx-react'
import {useIntl} from 'react-intl'
import {ArrowContainer, Popover} from 'react-tiny-popover'
import {useBasketStore, useCustomerStore, useGlobalStore, usePreBookStore} from '../../../../store/hooks/useStore'
import {BaseButton, Box, BoxProps, Button, Flex, Stack, Text} from '../../../../vanilla'
import {
  CheckIcon,
  ChevronDownIcon,
  CircleStarIcon,
  ClockAlertIcon,
  ExclamationCircleIcon,
} from '../../../../components/icons'
import {CredentialModalPlacementMap} from '../../../../types/ui'
import {ALCOHOL_PRODUCT_CLASS} from '../../../../utils/constants'
import {DeliverySlot, TimeSlot} from '../../../../types/store/basket'
import * as styles from '../../styles.css'
import {useCredentialModal} from '../../../../contexts'
import {CollapsablePanel} from '../../../../components/collapsable-panel'
import {useSlotExpiration} from '../../../../store/hooks/useSlotExpiration'
import {getDeviceType} from '../../../../utils/utils'
import {Countdown} from '../../../../components/book-delivery/reservation-status'
import {slotsMatch, slotBaseId} from '../../../../utils/booking-utils'
import useCustomSitePreference from '../../../../hooks/forms/useCustomSitePreference'
import {TimeSlotMoreInfoContent} from './time-slot-more-info'
import {baseThemeContract} from '../../../../vanilla/vars.css'
import { DeliveryTimeAndDate } from '../../../../components/book-delivery/delivery-time-and-date'

interface DeliveryDateSlotProps extends BoxProps<React.ElementType> {
  slot: TimeSlot
  deliveryDay: DeliverySlot
  onReservation: (slot: TimeSlot) => void
  vip: boolean
  smallBasketEnabled?: boolean
  isModal: boolean
  prebook: boolean
}

const DeliveryDateSlotComponent = (props: DeliveryDateSlotProps) => {
  const {smallBasketEnabled, vip, slot, prebook: isPrebook, onReservation, deliveryDay, isModal, style = {}} = props

  const {
    available,
    startHour,
    endHour,
    hasFreeDelivery,
    notReleasedStatus,
    shippingCost,
    preBookShippingCost,
    restrictedProductClasses,
    sameDayPastCutOff,
    sameDayCutOffDate,
    sameDayCutOffEditDate
  } = slot

  const {formatNumber} = useIntl()
  const {isRegistered} = useCustomerStore()
  const {currency} = useGlobalStore()
  const {currentDeliverySlotId, hasAlcoholItems, setOpenMiniBasketUI, basket, initMergingUI} = useBasketStore()
  const {isReservationExpired} = useSlotExpiration()
  const popoverRef = useRef<HTMLElement | undefined>(undefined)
  const credentialModal = useCredentialModal()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [pendingSlot, setPendingSlot] = useState<TimeSlot | null>(null)
  const {currentPreBookDeliverySlotId, preBookExpiry} = usePreBookStore()
  const isCurrentBookedSlot = slotsMatch(slot.id, isPrebook ? currentPreBookDeliverySlotId: currentDeliverySlotId)
  const isAlcoholProductRestrictedSlot = restrictedProductClasses?.includes(ALCOHOL_PRODUCT_CLASS)
  const isTimeSlotReservationExpired = isReservationExpired() && isCurrentBookedSlot
  const slotNotReleasedPreference = useCustomSitePreference('slotNotReleased', true)
  const shippingPrice = isPrebook ? preBookShippingCost ?? 0 : shippingCost ?? 0

  const baseStyle = {
    backgroundColor:
      !available || (hasAlcoholItems && isAlcoholProductRestrictedSlot) ? '#bebebe' : '#fff',
    minHeight: '60px',
  }

  useEffect(() => {
    if (isRegistered && pendingSlot) {
      onReservation(pendingSlot)

      setPendingSlot(null)
    }
  }, [isRegistered, pendingSlot, onReservation, initMergingUI])

  const handleSlotBooking = () => {
    const userAgentString = window.navigator.userAgent
    const deviceType = getDeviceType(userAgentString)

    const slotToReserve: TimeSlot =
      slot.isPrebookSlot && slot.prebookSlotId ? {...slot, id: slot.prebookSlotId} : slot

    if (isRegistered) {
      onReservation(slotToReserve)
    } else {
      setPendingSlot(slotToReserve)
      const modalPlacement =
        deviceType === 'mobile'
          ? CredentialModalPlacementMap.BOTTOM
          : CredentialModalPlacementMap.ATTACHED
      credentialModal.setModalPlacementType(modalPlacement)
      credentialModal.onToggle()
    }
  }

  const getBookActionButtonText = () => {
    const smallBasketChargeText =
      smallBasketEnabled && slot?.smallBasketCharge != null && slot?.smallBasketCharge != 0
        ? ' *'
        : ''

    if (isTimeSlotReservationExpired) {
      return 'Rebook'
    }

    if (slot.isPrebookSlot) {
      return 'Activate'
    }

    if (!isPrebook && (hasFreeDelivery || shippingCost === 0)) {
      return `Free Delivery${smallBasketChargeText}`
    }

    return `${formatNumber(shippingPrice, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}${smallBasketChargeText}`
  }

  const getNonAvailableSlotMessage = () => {
    if (isPrebook) {
      return ''
    }

    if (notReleasedStatus && slotNotReleasedPreference) {
      return 'Slot not yet released'
    }

    if (deliveryDay?.isToday && sameDayPastCutOff) {
      return `Same day delivery bookable up until ${sameDayCutOffDate}`
    }

    return 'Sorry this slot is no longer available'
  }

  return (
    <CollapsablePanel
      defaultOpen={false}
      panelContent={
        <TimeSlotMoreInfoContent
          smallBasketEnabled={smallBasketEnabled}
          smallBasketCharge={slot.smallBasketCharge}
          smallBasketChargeThreshold={slot.smallBasketChargeThreshold}
          deliveryCost={shippingCost}
          sameDayCutOffEditTime={sameDayCutOffEditDate}
        />
      }
    >
      {(moreInfoDisclosure) => (
        <Flex
          style={{
            ...baseStyle,
            ...style,
          }}
          paddingX={isModal ? '16px' : ['16px', '28px']}
          paddingY={['8px', '0px']}
          align="center"
          data-slot-id={slotBaseId(slot.id)}
        >
          <Flex
            width={['full', 'three-quarters']}
            direction={['column', 'row']}
            alignItems="center"
          >
            <Stack gap="4px" order="1" marginRight="auto" paddingRight="20px" minWidth="fit">
              <Text data-test-selector="time-slot">
                <DeliveryTimeAndDate
                  startHour={startHour}
                  endHour={endHour}
                  textProps={{
                    variant: 'text4',
                    color:
                      isCurrentBookedSlot
                        ? isTimeSlotReservationExpired
                          ? 'promotionColorDark'
                          : 'accent1'
                      : 'gray800'
                  }}
                />
              </Text>
              {vip ? (
                <Box className={styles.vipNote}>
                  <CircleStarIcon className={styles.vipToolTipStar} />
                  <Text>VIP Early Access</Text>
                </Box>
              ) : null}
            </Stack>
            {isCurrentBookedSlot && !isTimeSlotReservationExpired &&
              <Flex
                order={['0', '2']}
                width={['full', 'auto']}
                align="center"
                marginX={['0px', 'auto']}
              >
                <Text color="accent2" variant="text4" textAlign={['left']}>
                  Slot Reserved - Expires in{' '}
                  <Countdown
                    slotExpiration={(isPrebook ? preBookExpiry : basket?.c_slotExpiryTime) || ''}
                    color="accent2"
                    textVariant="text4"
                  />
                </Text>
              </Flex>
            }  
            {!isPrebook && hasAlcoholItems && isAlcoholProductRestrictedSlot && available ? (
              <Flex
                order={['0', '2']}
                width={['full', 'auto']}
                align="center"
                marginX={['0px', 'auto']}
              >
                <Text variant="text4">Restricted slot, please remove alcohol product to book</Text>
              </Flex>
            ) : (
              <>
                {!available ? (
                  <Flex order="2" width={['full', 'auto']} marginX={['0px', 'auto']}>
                    <Text
                      variant="text4"
                      order={['0', '2']}
                      style={{maxWidth: '400px', minWidth: '200px'}}
                    >
                      {getNonAvailableSlotMessage()}
                    </Text>
                  </Flex>
                ) : isTimeSlotReservationExpired ? (
                  <Flex order="2" width={['full', 'auto']} marginX={['0px', 'auto']}>
                  <Box gap="4px">
                    <ExclamationCircleIcon
                      color="promotionColorDark"
                      boxSize="16px"
                      display="inline-block"
                      marginRight="4px"
                    />
                    <Text
                      variant="text4"
                      color="promotionColorDark"
                      verticalAlign="bottom"
                      display="inline"
                      data-test-selector="delivery-reservation-expired"
                    >
                      Your delivery reservation has expired!
                    </Text>
                  </Box>
                  </Flex>
                ) : null}

                {available && isAlcoholProductRestrictedSlot && (
                  <Flex
                    gap="8px"
                    order="2"
                    align="center"
                    width={['full', 'auto']}
                    className={styles.deliveryTimeSlotMessage}
                  >
                    <Text variant="text4" color="promotionColorDark">
                      Alcohol restricted slot
                    </Text>
                    <Popover
                      parentElement={popoverRef.current}
                      content={({position, childRect, popoverRect}) => {
                        return (
                          <ArrowContainer
                            arrowColor="white"
                            arrowSize={10}
                            arrowStyle={{zIndex: baseThemeContract.zIndex.arrowContainer}}
                            childRect={childRect}
                            popoverRect={popoverRect}
                            position={position}
                          >
                            <Box
                              bg="white"
                              display="flex"
                              flexDirection="column"
                              justifyContent="center"
                              padding="16px"
                              borderRadius="base"
                              style={{boxShadow: '0 10px 25px 0 rgb(0 0 0 / 40%)'}}
                              textAlign="center"
                            >
                              <Stack>
                                <Text variant="heading0">Alcohol Licensing Restrictions</Text>
                                <Text variant="text3">
                                  Due to licensing laws, we are unable to deliver alcohol during this selected delivery time.
                                  <br /> 
                                  Availability is determined by local licensing hours and may vary by region.
                                </Text>
                              </Stack>
                            </Box>
                          </ArrowContainer>
                        )
                      }}
                      isOpen={isPopoverOpen}
                      positions={['top']}
                      align="center"
                      reposition={true}
                      containerStyle={{zIndex: baseThemeContract.zIndex.popover}}
                      onClickOutside={() => setIsPopoverOpen(false)}
                    >
                      <ExclamationCircleIcon
                        color="promotionColorDark"
                        onMouseEnter={() => setIsPopoverOpen(true)}
                        onMouseLeave={() => setIsPopoverOpen(false)}
                      />
                    </Popover>
                  </Flex>
                )}
                {deliveryDay?.isToday && available && (
                  <Flex
                    gap="4px"
                    order="3"
                    align={['flex-start', 'center']}
                    marginTop={['4px', '0px']}
                  >
                    <ClockAlertIcon width="24px" height="auto" marginRight="2px" />
                    <Flex
                      gap="4px"
                      marginTop="4px"
                      direction={['column', 'row']}
                      align={['flex-start', 'center']}
                    >
                      <Text
                        variant="text4"
                        lineHeight="none"
                      >{`Book before ${sameDayCutOffDate}`}</Text>
                      <Flex
                        align="center"
                        gap="4px"
                        cursor="pointer"
                        onClick={() => {
                          moreInfoDisclosure.onToggle()
                        }}
                      >
                        <Text variant="text1" textDecoration="underline" lineHeight="none">
                          More Info
                        </Text>
                        <ChevronDownIcon
                          style={{
                            transform: moreInfoDisclosure.isOpen ? 'rotate(180deg)' : 'unset',
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                )}
                {slot.isPrebookSlot && (
                  <Text variant="text4" order="3" marginRight={['auto', '10px']} color="accent0">
                    Your pre-booked delivery
                  </Text>
                )}
              </>
            )}
          </Flex>
          {available ? (
            <Flex
              justifyContent={
                isCurrentBookedSlot && !isTimeSlotReservationExpired ? 'center' : 'flex-end'
              }
              align="center"
              className={styles.deliveryButtonContainer}
              paddingLeft={['8px', '20px']}
            >
              {isAlcoholProductRestrictedSlot && hasAlcoholItems ? (
                <BaseButton
                  size="sm"
                  variant="secondary"
                  maxHeight="40px"
                  style={{width: '240px'}}
                  onClick={() => {
                    setOpenMiniBasketUI(true)
                  }}
                >
                  <Text
                    as="span"
                    variant="unstyled"
                    fontSize="inherit"
                    display={['none', 'inline']}
                  >
                    Edit Basket
                  </Text>
                  <Text
                    as="span"
                    variant="unstyled"
                    fontSize="inherit"
                    display={['inline', 'none']}
                  >
                    Edit
                  </Text>
                </BaseButton>
              ) : (
                <>  
                  {!isPrebook && isCurrentBookedSlot && !isTimeSlotReservationExpired ? (
                    <Flex
                      align={['flex-start', 'center']}
                      direction={['column', 'column', 'row']}
                      gap="4px"
                      alignItems={'center'}
                    >
                      <Flex direction="row" gap="4px">
                        <CheckIcon color="accent1" width="24px" height="24px" minWidth="24px" />
                        <Text variant="text4" color="accent1" textAlign="center">
                          Booked{' '}
                        </Text>
                      </Flex>
                      <Text color="accent2" variant="text3" textAlign={['center', 'left']}>
                        - Expires in{' '}
                        <Countdown
                          slotExpiration={(isPrebook ? preBookExpiry : basket?.c_slotExpiryTime) || ''}
                          color="accent2"
                          textVariant="text3"
                        />
                      </Text>
                    </Flex>
                  ) : (
                    <Button
                      size="sm"
                      variant="primary"
                      maxHeight="40px"
                      style={{ width: '240px' }}
                      onClick={handleSlotBooking}
                      data-test-selector="book-slot"
                    >
                      {getBookActionButtonText()}
                    </Button>
                  )}
                </>
              )}
            </Flex>
          ) : null}
        </Flex>
      )}
    </CollapsablePanel>
  )
}

export const DeliveryDateSlot = observer(DeliveryDateSlotComponent)
